import { useCallback, useRef } from 'react';
import {
  cancelTerminalPayment,
  collectTerminalPayment,
  getTerminalPaymentStatus,
} from '../../redux/actions/payment';

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 48; // ~2 minutes
const TERMINAL_NOT_FOUND_MESSAGE = 'Payment terminal Not found';

export const TERMINAL_OUTCOME = {
  APPROVED: 'approved',
  DECLINED: 'declined',
  TIMEOUT: 'timeout',
  CANCELLED: 'cancelled',
  NOT_CONFIGURED: 'not_configured', // this store has no terminal registered - caller falls back to Sticky
};

// Owns the collect -> poll -> resolve lifecycle for a per-store terminal
// payment. Which gateway actually handles it (Worldpay, Dojo, etc.) is
// resolved on the backend from the store's own registered terminal - this
// hook and its caller never know or need to know which one it is. Callers
// only ever deal with one awaited call and its outcome; polling, timers and
// cancellation are entirely internal.
export function useTerminalPayment(onReferenceChange) {
  const activeRef = useRef(false);
  const timerRef = useRef(null);
  const resolverRef = useRef(null);
  const referenceRef = useRef(null);

  const setReference = useCallback((reference) => {
    referenceRef.current = reference;
    if (onReferenceChange) onReferenceChange(reference);
  }, [onReferenceChange]);

  const stopPolling = useCallback(() => {
    activeRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const pollUntilResolved = useCallback(
    (reference) =>
      new Promise((resolve) => {
        activeRef.current = true;
        resolverRef.current = resolve;
        let attempts = 0;

        const finish = (outcome, message, card) => {
          stopPolling();
          resolverRef.current = null;
          resolve({ outcome, ...(message ? { message } : {}), ...(card ? { card } : {}) });
        };

        const poll = async () => {
          if (!activeRef.current) return;
          attempts += 1;

          if (attempts > MAX_POLL_ATTEMPTS) {
            finish(TERMINAL_OUTCOME.TIMEOUT);
            return;
          }

          let res;
          try {
            res = await getTerminalPaymentStatus(reference);
          } catch (err) {
            // A transient network blip just retries - the local order/session
            // state hasn't changed, only the read of it failed.
            timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
            return;
          }

          if (!activeRef.current) return;

          if (res?.status === 'approved') {
            finish(TERMINAL_OUTCOME.APPROVED, null, res.card);
          } else if (res?.status === 'pending') {
            timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          } else {
            // declined / error / cancelled from the gateway side
            finish(TERMINAL_OUTCOME.DECLINED, res?.reason);
          }
        };

        poll();
      }),
    [stopPolling]
  );

  // Resolves once the payment reaches a final state (approved/declined/
  // timeout), or immediately if this store has no terminal registered.
  const collect = useCallback(
    async ({ orderId, storeId, deviceToken }) => {
      if (!storeId) return { outcome: TERMINAL_OUTCOME.NOT_CONFIGURED };

      const res = await collectTerminalPayment({ orderId, storeId, deviceToken });

      if (!res?.ack) {
        if (res?.message === TERMINAL_NOT_FOUND_MESSAGE) {
          return { outcome: TERMINAL_OUTCOME.NOT_CONFIGURED };
        }
        return { outcome: TERMINAL_OUTCOME.DECLINED, message: res?.message };
      }

      if (res.status === 'approved') {
        return { outcome: TERMINAL_OUTCOME.APPROVED, card: res.card };
      }

      setReference(res.reference);
      const result = await pollUntilResolved(res.reference);
      setReference(null);
      return result;
    },
    [pollUntilResolved, setReference]
  );

  // Voids whatever is currently in flight and unblocks an awaited collect().
  const cancelPending = useCallback(async () => {
    const reference = referenceRef.current;
    stopPolling();
    setReference(null);

    if (reference) {
      try {
        await cancelTerminalPayment({ reference });
      } catch (err) {
        // Best-effort - the transaction record still exists for manual
        // reconciliation if the void call itself fails.
      }
    }

    if (resolverRef.current) {
      resolverRef.current({ outcome: TERMINAL_OUTCOME.CANCELLED });
      resolverRef.current = null;
    }
  }, [setReference, stopPolling]);

  return { collect, cancelPending };
}
